/**
 * BrandingTokens V2 — Shared Entry (canonical).
 *
 * Phase A: single source of truth for the V2-Shape. Backend (`src/branding/
 * tokens.types.ts`) and Portal (`portal/src/lib/branding/tokens.types.ts`)
 * are re-export shims that forward to this file.
 *
 * Sub-types (`LogoPosition`, `LogoVariant`, …) are **exported** so Portal
 * and Backend can consume them directly from `@scrolly/branding`. The V1
 * form-layer `LogoPosition` in `./types.ts` was renamed to
 * `FormLogoPosition` to avoid the historic naming collision
 * (V1 = `'left' | 'center' | 'hidden'`, V2 = `'L' | 'M' | 'R'`).
 */

export type LogoPosition = 'L' | 'M' | 'R';

export type LogoVariant = {
  url: string | null;
  position: LogoPosition;
};

export type BrandingEasing =
  | 'linear'
  | 'in-cubic'
  | 'out-cubic'
  | 'in-out-cubic'
  | 'spring';

export type ShadowDepth = 0 | 1 | 2 | 3;

export type WatermarkPosition =
  | 'tl' | 'tm' | 'tr'
  | 'ml' | 'mm' | 'mr'
  | 'bl' | 'bm' | 'br';

export interface BrandingTokens {
  version: 2;
  logo: {
    light: LogoVariant;
    dark: LogoVariant;
    favicon: { url: string | null };
  };
  identity: {
    orgName: string;
    urlSlug: string;
  };
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    warn: string;
    bg: string;
    surface: string;
    border: string;
    muted: string;
  };
  typography: {
    display: { family: string; customFontId?: string | null };
    ui: { family: string; customFontId?: string | null };
  };
  shape: {
    cornerRadius: number;
    shadowDepth: ShadowDepth;
  };
  motion: {
    duration: number;
    easing: BrandingEasing;
  };
  /**
   * Phase 13 — Opt-in flag for org-logo rendering in the Public-Viewer
   * colophon. Default false. Scrolly overrides can flip this.
   */
  showLogoInViewer: boolean;
  social?: { ogTemplate?: string; ogBackgroundUrl?: string | null };
  watermark?: {
    enabled: boolean;
    position: WatermarkPosition;
    content?: string;
  };
  credit?: { defaultAuthorName: string; defaultAuthorUrl: string };
}

/**
 * Per-Scrolly Override-Payload für BrandingTokens.
 *
 * Phase A canonical shape. Deckt die Backend-Semantik (Mapped-Type über
 * alle Top-Level-Keys außer `version`) und den Portal-Use-Case
 * (explizite Sektionen + `showLogoInViewer`-Flip) in einer einzigen
 * Definition. `version` ist bewusst ausgeschlossen, weil die Schema-
 * Version nicht per Override überschreibbar sein soll.
 */
export type BrandingTokensOverrides = {
  [K in keyof Omit<BrandingTokens, 'version'>]?: BrandingTokens[K] extends object
    ? Partial<BrandingTokens[K]>
    : BrandingTokens[K];
};

/**
 * Response-Shape des Backend-Endpoints `GET /scrollys/:id/branding/tokens`.
 * Bündelt Org-Default, Scrolly-Overrides und das resultierende Effective
 * (Org ∪ Overrides) in einer Antwort — Portal-Editor nutzt das zum Diffen
 * und Initialisieren des Form-States.
 */
export interface ScrollyTokensResponse {
  org: BrandingTokens;
  overrides: BrandingTokensOverrides | Record<string, unknown>;
  effective: BrandingTokens;
}

export const BRANDING_DEFAULTS: BrandingTokens = {
  version: 2,
  logo: {
    light: { url: null, position: 'L' },
    dark: { url: null, position: 'L' },
    favicon: { url: null },
  },
  identity: { orgName: '', urlSlug: '' },
  colors: {
    primary: '#F02873',
    secondary: '#322832',
    accent: '#96C81E',
    warn: '#FF9600',
    bg: '#FAF9F7',
    surface: '#FFFFFF',
    border: '#E0DCDA',
    muted: '#706C6A',
  },
  typography: {
    display: { family: 'Instrument Serif', customFontId: null },
    ui: { family: 'DM Sans', customFontId: null },
  },
  shape: { cornerRadius: 10, shadowDepth: 2 },
  motion: { duration: 240, easing: 'out-cubic' },
  showLogoInViewer: false,
};
